import React from "react";
import classNames from "classnames";

import { useAdsContext } from "./AdsContext";
import { PinnedAdTile, PinnedAdTileProps } from "./PinnedAdTile";

type InjectableProps = Pick<PinnedAdTileProps, "onClick" | "track" | "spaceId">;

export type PinnedAdsListProps = InjectableProps & {
  /**
   * Optional cap applied on top of the server-side
   * `config.spacePinnedMax`. Defaults to the config value, which is the
   * intended behavior — callers rarely need to override this.
   */
  maxOverride?: number;
  /**
   * Optional className for the outer container. Callers pass this when
   * embedding the list inside a specific layout.
   */
  className?: string;
};

/**
 * Renders the current set of pinned ads as a vertical list of
 * `PinnedAdTile`s. Reads the list + config directly from
 * `AdsContext`, so the only prerequisite is that an
 * `AdsContextProvider` is mounted somewhere above in the tree.
 *
 * The component renders nothing (returns `null`) when any of the
 * following is true:
 *   - `config.spacePinnedEnabled` is `false` (server kill switch).
 *   - the `pinnedAds` array is empty (either no eligible ads or the
 *     subscription gate blocked the fetch).
 *
 * Layout: the list is a plain vertical flex column. Each tile carries
 * its own row geometry (see `PinnedAdTile.scss`); the wrapper does
 * NOT reuse `.highnote-base-table` or `.entity-table` classes so it
 * does not participate in sibling selectors targeting the real
 * `EntityTable` rendered below it.
 *
 * Callers MUST safely render `null` — do not wrap this in a visible
 * container with a title/skeleton; there must be zero footprint when
 * there are no pinned ads.
 */
export const PinnedAdsList = ({
  onClick,
  track,
  spaceId,
  maxOverride,
  className,
}: PinnedAdsListProps = {}) => {
  const { pinnedAds, config } = useAdsContext();

  if (!config.spacePinnedEnabled) return null;
  if (pinnedAds.length === 0) return null;

  // The server has already capped at `config.spacePinnedMax`, but we
  // re-apply the cap client-side as defense-in-depth. This also makes
  // `maxOverride` take effect without re-fetching.
  const cap = Math.min(
    typeof maxOverride === "number" && maxOverride >= 0
      ? maxOverride
      : config.spacePinnedMax,
    config.spacePinnedMax,
  );
  const visible = pinnedAds.slice(0, cap);
  if (visible.length === 0) return null;

  return (
    <div
      className={classNames("highnote-pinned-ads-list", className)}
      data-testid="pinned-ads-list"
    >
      {visible.map((ad) => (
        <PinnedAdTile
          key={ad.id}
          ad={ad}
          onClick={onClick}
          track={track}
          spaceId={spaceId}
        />
      ))}
    </div>
  );
};
